/**
 * Jobs Bids store – GET jobs/bids/ once per session per page.
 * Refetch after POST (submit bid) in Bids tab.
 */

import { create } from "zustand";
import apiClient from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";

export interface JobsBidsApiJob {
  id: number;
  title: string;
  tbl: string;
  date: string;
  location: string;
  description: string;
  duration: number;
  audience: number;
  event_type: string;
  budget: string;
  is_applied: boolean;
  is_liked: boolean;
  pitched_artists: number;
  note?: string;
  bid: {
    coins_deducted: number;
    amount: number;
    applied_date?: string;
    status: string;
    negotiation_stats?: { used: number; remaining: number };
    negotiations?: unknown[];
  };
}

interface JobsBidsState {
  results: JobsBidsApiJob[];
  count: number;
  lastFetchedPage: number | null;
  hasFetchedInSession: boolean;
  isLoading: boolean;
  error: string | null;

  fetchBids: (page: number) => Promise<void>;
  ensureFetched: (page: number) => Promise<void>;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlightByPage: Record<number, Promise<void>> = {};

const initialState = {
  results: [],
  count: 0,
  lastFetchedPage: null as number | null,
  hasFetchedInSession: false,
  isLoading: false,
  error: null as string | null,
};

export const useJobsBidsStore = create<JobsBidsState>()((set, get) => ({
  ...initialState,

  fetchBids: async (page: number) => {
    const existing = inFlightByPage[page];
    if (existing) return existing;
    const promise = (async () => {
      set({ isLoading: true, error: null });
      try {
        const url =
          page === 1
            ? API_ENDPOINTS.AUTH.JOBS_BIDS
            : `${API_ENDPOINTS.AUTH.JOBS_BIDS}?page=${page}`;
        const res = await apiClient.get<{
          success?: boolean;
          data?: { results?: JobsBidsApiJob[]; count?: number };
        }>(url);
        const raw = res.data?.data ?? res.data;
        const data = raw as { results?: JobsBidsApiJob[]; count?: number } | undefined;
        const results = data?.results ?? [];
        const count = data?.count ?? 0;
        set({
          results,
          count,
          lastFetchedPage: page,
          hasFetchedInSession: true,
          isLoading: false,
          error: null,
        });
      } catch {
        set({
          results: [],
          count: 0,
          isLoading: false,
          error: "Failed to load bids",
        });
      } finally {
        delete inFlightByPage[page];
      }
    })();
    inFlightByPage[page] = promise;
    return promise;
  },

  ensureFetched: async (page: number) => {
    const { hasFetchedInSession, lastFetchedPage, fetchBids } = get();
    if (hasFetchedInSession && lastFetchedPage === page) return;
    return fetchBids(page);
  },

  setError: (error) => set({ error }),

  reset: () => {
    inFlightByPage = {};
    set(initialState);
  },
}));
